import React, { useEffect, useMemo, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, ReferenceArea, Cell, ResponsiveContainer, Tooltip,
} from 'recharts';
import { ChevronDown, ChevronUp, TrendingUp } from 'lucide-react';
import { clientPortalService } from '../../services/clientPortal.service';
import { useCart } from '../../contexts/CartContext';

const toDateOnly = (d) => {
  if (!d) return null;
  if (typeof d === 'string') return d.slice(0, 10);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/**
 * Collapsible "Order vs. your history" panel for Checkout. Renders only when at
 * least one cart line has a historical suggestion. Per line: a horizontal bar
 * comparing the ordered qty vs. average sold, with a min–max range band, plus a
 * "Use suggested" shortcut.
 */
export const OrderHistoryComparison = ({ cart, deliveryDate }) => {
  const { setItemQuantity } = useCart();
  const [suggMap, setSuggMap] = useState({});
  const [open, setOpen] = useState(true);
  const dateStr = toDateOnly(deliveryDate);

  useEffect(() => {
    let active = true;
    if (!dateStr) return undefined;
    (async () => {
      try {
        const res = await clientPortalService.getOrderInsights(dateStr);
        if (!active) return;
        const map = {};
        (res.suggestions || []).forEach((s) => { map[s.product_id] = s; });
        setSuggMap(map);
      } catch {
        // Insights are optional — never block checkout.
      }
    })();
    return () => { active = false; };
  }, [dateStr]);

  const lines = useMemo(
    () => (cart || []).map((it) => ({ item: it, s: suggMap[it.id] })).filter((x) => x.s),
    [cart, suggMap]
  );

  if (lines.length === 0) return null;

  return (
    <div className="rounded-xl border border-amber-100 bg-white shadow-sm" data-testid="order-history-comparison">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3"
        data-testid="order-history-toggle"
      >
        <span className="flex items-center gap-2 font-semibold text-gray-900">
          <TrendingUp className="h-4 w-4 text-amber-600" /> Order vs. your history
        </span>
        {open ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-4">
          <p className="text-xs text-gray-500">
            The amber band shows what you usually sell. Blue is your current order.
          </p>
          {lines.map(({ item, s }) => {
            const qty = item.quantity;
            const domainMax = Math.ceil((Math.max(qty, s.max_sold, s.suggested_qty) || 1) * 1.15);
            const data = [
              { label: 'Avg sold', value: s.avg_sold, key: 'avg' },
              { label: 'Your order', value: qty, key: 'you' },
            ];
            return (
              <div key={item.id} className="border-b last:border-b-0 pb-3 last:pb-0" data-testid={`history-line-${item.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm font-medium text-gray-900 truncate pr-2">{item.name}</p>
                  {qty !== s.suggested_qty && (
                    <button
                      className="shrink-0 text-xs font-medium text-amber-700 hover:underline"
                      onClick={() => setItemQuantity(item.id, s.suggested_qty)}
                      data-testid={`use-suggested-${item.id}`}
                    >
                      Use suggested ({s.suggested_qty})
                    </button>
                  )}
                </div>
                <div style={{ width: '100%', height: 68 }}>
                  <ResponsiveContainer>
                    <BarChart layout="vertical" data={data} margin={{ top: 4, right: 16, bottom: 4, left: 8 }} barCategoryGap={6}>
                      <XAxis type="number" domain={[0, domainMax]} hide />
                      <YAxis type="category" dataKey="label" width={62} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                      <ReferenceArea x1={s.min_sold} x2={s.max_sold} fill="#f59e0b" fillOpacity={0.12} />
                      <Tooltip formatter={(v) => [v, 'qty']} cursor={{ fill: 'transparent' }} />
                      <Bar dataKey="value" radius={[3, 3, 3, 3]} isAnimationActive={false} label={{ position: 'right', fontSize: 11 }}>
                        {data.map((d) => <Cell key={d.key} fill={d.key === 'you' ? '#2563eb' : '#f59e0b'} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <p className="text-xs text-gray-500">
                  Based on your last {s.sample_size} {s.basis === 'weekday' ? 'same-weekday' : 'recent'} deliveries · range {s.min_sold}–{s.max_sold}
                  {s.sell_through_rate != null ? ` · ${Math.round(s.sell_through_rate * 100)}% sell-through` : ''}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OrderHistoryComparison;
